import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import PublicLayout from "@/components/layout/PublicLayout";
import PageHeader from "@/components/PageHeader";
import { useAuth } from "@/context/AuthContext";
import { trackEvent, formatApiErrorDetail } from "@/lib/api";

export default function EmployerRegistration() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) return toast.error("Please accept the terms to continue.");
    setLoading(true);
    try {
      await register({ ...form, role: "employer" });
      trackEvent("employer_registration");
      toast.success("Employer account created!");
      navigate("/employer/dashboard");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Registration failed"); }
    finally { setLoading(false); }
  };

  return (
    <PublicLayout>
      <Seo title="Employer Registration" description="Create an employer account with Dreamzz HR & Consultancy to post jobs and hire talent across Karnataka." noindex />
      <PageHeader eyebrow="For Employers" title="Create Your Employer Account" subtitle="Post jobs, review applicants and build your team with Dreamzz HR." />
      <section className="max-w-md mx-auto px-4 sm:px-6 py-12">
        <form onSubmit={submit} className="glass-strong rounded-2xl p-6 sm:p-8 grid gap-4">
          <div><Label>Company Name *</Label><Input data-testid="emp-reg-company" required value={form.name} onChange={upd("name")} className="bg-white/5 border-white/10 mt-1" /></div>
          <div><Label>Work Email *</Label><Input data-testid="emp-reg-email" type="email" required value={form.email} onChange={upd("email")} className="bg-white/5 border-white/10 mt-1" /></div>
          <div><Label>Phone *</Label><Input data-testid="emp-reg-phone" required value={form.phone} onChange={upd("phone")} className="bg-white/5 border-white/10 mt-1" /></div>
          <div><Label>Password *</Label><Input data-testid="emp-reg-password" type="password" required minLength={6} value={form.password} onChange={upd("password")} className="bg-white/5 border-white/10 mt-1" /></div>
          <label className="flex items-start gap-3 text-sm text-slate-400">
            <Checkbox data-testid="emp-reg-consent" checked={consent} onCheckedChange={setConsent} className="mt-0.5" />
            <span>I accept the Privacy Policy and Terms of Service.</span>
          </label>
          <Button data-testid="emp-reg-submit" type="submit" disabled={loading} className="btn-press bg-brand-emerald hover:bg-[#C62828] text-white font-semibold h-12">
            {loading ? "Creating..." : "Create Employer Account"}
          </Button>
          <p className="text-center text-sm text-slate-400">Already have an account? <a href="/login" className="text-brand-emerald">Login</a></p>
        </form>
      </section>
    </PublicLayout>
  );
}
